import * as React from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useForm } from 'react-hook-form';
import { Modal, ModalContent } from '@restworld/ui-ds-base';

import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import RestaurantOnboarding from './restaurant-onboarding';
import { RestaurantOnboardingFooter } from './restaurant-onboarding-footer';
import { googleAddresInputValueAtom } from './google-address-input-result';
import { shouldShowOnboardingFooter } from './should-show-onboarding-footer';
import { cn } from '@restworld/utils-common';
import {
  useRestaurantOnboardingFormReset,
  useRestaurantOnboardingResetActions,
} from './restaurant-onboarding-modal.hooks';
import {
  activeRestaurantOnboardingStateAtom,
  isManualEntryAtom,
  manualAddressFieldsAtom,
  restaurantOnboardingModalVisibleAtom,
} from './restaurant-onboarding-atoms';

export * from './restaurant-onboarding-atoms';

export const restaurantOnboardingFormDefaultValues = {
  restaurant_name: '',
  address: '',
  brief_description: '',
  covers_per_service: '',
  instagram_url: '',
  facebook_url: '',
  website_url: '',
  the_fork_url: '',
  opening_hours: '',
  number_of_staffs: '',
  name: '',
  surname: '',
  email: '',
  phone_number: '',
  tracking_consent: false,
  terms_and_conditions: false,
  vexatious_clauses_acceptance: false,
};

// Main Component modal for authenticated user
const RestaurantOnboardingModal: React.FunctionComponent = () => {
  const restaurantOnboardingModalVisible = useAtomValue(restaurantOnboardingModalVisibleAtom);
  const [activeUI, setActiveUI] = useAtom(activeRestaurantOnboardingStateAtom);

  const methods = useForm<RestaurantOnboardingPayload>({ defaultValues: restaurantOnboardingFormDefaultValues });

  const { resetState, handleModalClose } = useRestaurantOnboardingResetActions();
  const setIsManualEntry = useSetAtom(isManualEntryAtom);
  const setManualAddressFields = useSetAtom(manualAddressFieldsAtom);

  const handleBack = React.useCallback(() => {
    if (activeUI === 'restaurant_already_exists' || activeUI === 'restaurant_onboarding_form') {
      setActiveUI('select_restaurant_from_google');
      setIsManualEntry(false);
      setManualAddressFields({});
    } else {
      resetState();
    }
  }, [activeUI, resetState, setActiveUI, setIsManualEntry, setManualAddressFields]);
  useRestaurantOnboardingFormReset({ methods });

  const isRestaurantOnboardingSuccessful = activeUI === 'restaurant_onboarding_successfull';
  const googleInputAddressValue = useAtomValue(googleAddresInputValueAtom);
  const shouldShowFooter = React.useMemo(
    () => shouldShowOnboardingFooter(activeUI, googleInputAddressValue.length > 0),
    [activeUI, googleInputAddressValue]
  );
  const modalContentClassName = React.useMemo(() => {
    const classes = ['overflow-x-hidden'];
    if (isRestaurantOnboardingSuccessful) classes.push('bg-purple-50');
    return classes.join(' ');
  }, [isRestaurantOnboardingSuccessful]);

  return (
    <Modal open={restaurantOnboardingModalVisible} onOpenChange={(v) => !v && handleModalClose()}>
      <ModalContent
        size="3xl"
        showBackButton={activeUI === 'restaurant_onboarding_form' || activeUI === 'restaurant_already_exists'}
        onBack={handleBack}
        className={modalContentClassName}
        footer={shouldShowFooter ? <RestaurantOnboardingFooter methods={methods} /> : undefined}
        noPadding={isRestaurantOnboardingSuccessful}
        hideHeader={isRestaurantOnboardingSuccessful}
      >
        <RestaurantOnboardingWrapperStyled>
          <RestaurantOnboarding methods={{ ...methods }} />
        </RestaurantOnboardingWrapperStyled>
      </ModalContent>
    </Modal>
  );
};

export default RestaurantOnboardingModal;
const RestaurantOnboardingWrapperStyled = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full', className)} {...props} />
);
